import { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useLocale, useT, withLocale } from '../i18n';
import { api, setAccessToken } from '../api/client';
import { useApiData } from '../api/useApiData';
import AsyncSection from '../components/shared/AsyncSection';

type Request = { id: string; purpose: string; status: string; createdAt: string };
type AssessmentSession = { id: string; track: string; status: string; report?: { score: number; maturityLevel: number } | null };

/* Real, auth-gated portal dashboard (spec §17) — replaces the old
 * static site's fixed "demo mode" mock cards with the caller's actual
 * org-scoped data from /api/v1/portal/*. Falls back to a clear
 * sign-in prompt (not silent redirect-with-no-explanation) if no
 * session token is present. */
export default function PortalDashboard() {
  const locale = useLocale();
  const t = useT();
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem('cyrkil_access_token');
    if (token) {
      setAccessToken(token);
      setAuthed(true);
    }
    setReady(true);
  }, []);

  // Guarded on `authed` so this never fires (and never logs a spurious
  // 401) before the sign-in redirect below has a chance to happen —
  // the redirect itself only runs after hooks, per Rules of Hooks.
  const requestsState = useApiData(
    () => (authed ? api.get<{ data: Request[] }>('/api/v1/portal/requests').then((r) => r.data) : Promise.resolve([])),
    [authed],
    (d) => d.length === 0
  );
  const assessmentsState = useApiData(
    () => (authed ? api.get<{ data: AssessmentSession[] }>('/api/v1/portal/assessments').then((r) => r.data) : Promise.resolve([])),
    [authed],
    (d) => d.length === 0
  );

  function signOut() {
    sessionStorage.removeItem('cyrkil_access_token');
    setAccessToken(null);
    setAuthed(false);
  }

  if (!ready) return null;
  if (!authed) return <Navigate to={withLocale('/portal-login', locale)} replace />;

  return (
    <section className="section" style={{ paddingTop: 160 }}>
      <div className="container">
        <div className="section-head reveal is-visible" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <span className="eyebrow">{t('portal.eyebrow')}</span>
            <h1>{t('portal.welcome')}</h1>
          </div>
          <button type="button" className="btn btn-ghost" onClick={signOut}>
            {t('portal.sign_out')}
          </button>
        </div>

        <div className="card reveal is-visible" style={{ marginBottom: 24 }}>
          <h3>{t('portal.requests_headline')}</h3>
          <AsyncSection
            state={requestsState}
            emptyMessage="No requests raised yet from this account."
            render={(items) => (
              <ul>
                {items.map((r) => (
                  <li key={r.id} className="body-sm">
                    {r.purpose} — {r.status}
                  </li>
                ))}
              </ul>
            )}
          />
          <Link className="btn btn-secondary" to={withLocale('/contact', locale)} style={{ marginTop: 16, display: 'inline-block' }}>
            {t('portal.raise_request')}
          </Link>
          <p className="body-sm" style={{ opacity: 0.75, marginTop: 8 }}>
            {t('portal.raise_request_note')}
          </p>
        </div>

        <div className="card reveal is-visible" style={{ marginBottom: 24 }}>
          <h3>{t('portal.nav.assessments')}</h3>
          <AsyncSection
            state={assessmentsState}
            emptyMessage="No Digital 360 assessments completed yet for this organization."
            render={(items) => (
              <ul>
                {items.map((a) => (
                  <li key={a.id} className="body-sm">
                    {a.track} — {a.status}
                    {a.report ? ` — score ${Math.round(a.report.score)} (level ${a.report.maturityLevel})` : ''}
                  </li>
                ))}
              </ul>
            )}
          />
          <Link className="btn btn-secondary" to={withLocale('/assessment', locale)} style={{ marginTop: 16, display: 'inline-block' }}>
            Start a new assessment
          </Link>
        </div>
      </div>
    </section>
  );
}
